import { Tenant, UserPhoneNumber } from '@wix/answers-api';
import { Logger, withLogger } from './bi';
import { TranslateFn } from '@wix/answers-lib';

export enum ErrorCodes {
	INVALID_WIX_TOKEN = -50004
}

export enum SignupType {
	WIX = 'wix',
	EMAIL = 'email'
}

export interface NewTenantParams {
	userEmail: string;
	userName: string;
	userPassword: string;
	organizationName: string;
	phoneNumber?: UserPhoneNumber;
	subdomain: string;
	locale: string;
}

export type NewWixTenantParams = {
	userName: string;
	organizationName: string;
	phoneNumber?: UserPhoneNumber;
	subdomain: string;
	locale: string;
	email: string;
	token: string;
};

export interface NewTicketNotificationParams {
	userEmail: string;
	userName: string;
	ticketSubject: string;
	ticketUrl: string;
	ticketContent: string;
	isCCRequest: boolean;
}

export type NewTicketReplyNotificationParams = {
	userEmail: string;
	userName: string;
	ticketSubject: string;
	ticketUrl: string;
	ticketContent: string;
	replyContent: string;
};

export type PurchasedCreditsParams = {
	defaultHost: string;
	selectedAmount: number;
	currentBalance: number;
};

export type UpgradeParams = {
	defaultHost: string;
	maxPeopleOnTeam: number;
	planType: string;
	selectedFrequency: string;
	price: number;
	ownerEmail?: string;
	provider: string;
};

export type TenantCreation = {
	tenant: Tenant;
	token: string;
};

export type withTranslator = {
	t: TranslateFn;
};

export type MarketingBaseProps = withLogger & withTranslator;

export type TenantCreationPayload = TenantCreation | Tenant;

export const isTenantCreation = (
	bob: Tenant | TenantCreation
): bob is TenantCreation => {
	const benny = bob as any;
	return benny.tenant && benny.token;
};

// server logging - not yet!
export const createServerLogger = (): Logger => {
	return {
		logEvent: () => {
			return null as any;
		},
		logSignupSuccess: () => {
			return null as any;
		}
	};
};

export const isDebugClient = () => {
	try {
		return localStorage.getItem('mode') === 'debug';
	} catch (e) {
		return false;
	}
};

export const makeCustomError = (errorCode: number, message: string) => {
	return { errorCode, message, expose: true };
};

export const getPathname = (req: any) => {
	try {
		return req.url.replace(/[?].*/g, '');
	} catch (e) {
		return '';
	}
};

export const isDebugMode = (url: string) => {
	return url.includes('mode=debug');
};

export const maybeGetDebugBundle = (url: string) =>
	isDebugMode(url) ? 'bundle' : 'bundle.min';

export const SPECIAL_MARKETING_SIGNUP_PATH = '/mk-signup';
export const TECH_ASSESSMENT_PATH = '/assessment';
export const GENERAL_META_DESCRIPTION_TEXT = 'Wix Answers puts all support channels on the same platform. Knowledge base, tickets, chats, and calls work together as one.';
export const TECH_ASSESSMENT_META_DESCRIPTION_TEXT = 'Grade your support tech stack in minutes. See how you rank against top industry pain points and get tips on how to improve your tools and efficiency.';
export const GENERAL_SHARE_LINK_IMAGE = 'https://static.wixstatic.com/media/9c185e_324758405f504c01b7503e347b3fb11c~mv2.png';
export const TECH_ASSESSMENT_SHARE_LINK_IMAGE = 'https://static.wixstatic.com/media/dba05e_4314e7b68fbf4b5387f5a7035f0713d3~mv2.jpg';

const NON_BUSINESS_EMAIL_DOMAINS = [
	'@gmail',
	'@yahoo',
	'@hotmail',
	'@aol',
	'@outlook',
	'@icloud',
	'@comcast',
	'@msn',
	'@example',
	'@mail',
	'@yandex',
	'@att.net',
];

export const isNotBusinessEmailAddress = (email: string) => {
	const lowerCaseEmail = email.toLowerCase();
	return NON_BUSINESS_EMAIL_DOMAINS.some((emailDomain: string) => lowerCaseEmail.includes(emailDomain));
};

export const NON_BUSINESS_EMAIL_ERROR_STR = 'please provide a business email address.';
export const BUSINESS_EMAIL_LABEL = 'Business Email';
export const SUBDOMAIN_LABEL = 'Your Wix Answers subdomain';
